/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, retry, switchMap, takeUntil } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import { isNull } from 'lodash';
import { SESSION_STORAGE_MISSION_PLAN_ID } from './reducer';

const requestMissionPlansSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedMissionPlansSuccess.create(response);
};

const requestMissionPlansError = (error: AjaxError): any => {
  return ActionCreators.ReceivedMissionPlansError.create(
    error.message !== undefined ? error.message : 'Error requesting mission plans',
  );
};

const savingMissionPlanSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.SavedMissionPlanSuccess.create(response);
};

const setMissionPlanId = (response: AjaxResponse<any>): any => {
  return ActionCreators.SetSelectedMissionPlanId.create(response.response.id);
};

const savingMissionPlanError = (error: AjaxError): any => {
  return ActionCreators.SavedMissionPlanError.create(
    error.message !== undefined ? error.message : 'Error saving mission plan',
  );
};

const requestMissionPlanByIdSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedMissionPlanByIdSuccess.create(response);
};

const requestMissionPlanByIdError = (error: AjaxError): any => {
  return ActionCreators.ReceivedMissionPlanByIdError.create(
    error.message !== undefined ? error.message : 'Error receiving mission plan by id',
  );
};

const deleteMissionPlanSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.DeletedMissionPlanSuccess.create(response);
};

const deleteMissionPlanError = (error: AjaxError): any => {
  return ActionCreators.DeletedMissionPlanError.create(
    error.message !== undefined ? error.message : 'Error deleting mission plan',
  );
};

const requestHnaOverrideDataSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedHnaOverrideDataSuccess.create(response);
};

const requestHnaOverrideDataError = (error: AjaxError): any => {
  return ActionCreators.ReceivedHnaOverrideDataError.create(
    error.message !== undefined ? error.message : 'Error requesting HNA override data',
  );
};

export const getMissionPlansEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestMissionPlans.type),
    switchMap(($action: any): Observable<Action> => {
      if (isNull($action.payload.datapathUuid)) {
        return ajaxRaw.getWithHeaders('/mpt/missionplans/byuser', { userid: $action.payload.userId }).pipe(
          map((response: AjaxResponse<any>): Action => requestMissionPlansSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestMissionPlansError(error))
              ),
          ),
        );
      } else {
        return ajaxRaw.post('/mpt/missionplans/bydatapathuuid', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => requestMissionPlansSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestMissionPlansError(error))
              ),
          ),
        );
      }
    }),
  );

export const saveMissionPlanEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.SaveMissionPlan.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .postWithHeaders('/mpt/missionplans', $action.payload, {
            userid: $action.payload.user,
            missionPlanId: $action.payload.id,
          })
          .pipe(
            mergeMap((response: AjaxResponse<any>): Action[] => [
              savingMissionPlanSuccess(response),
              setMissionPlanId(response),
            ]),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(savingMissionPlanError(error))
                ),
            ),
          ),
    ),
  );

export const getMissionPlanByIdEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.GetMissionPlanById.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .getWithHeaders(
            '/mpt/missionplans',
            $action.payload.missionPlanId
              ? { missionPlanId: $action.payload.missionPlanId, userid: $action.payload.userId }
              : { userid: $action.payload.userId },
          )
          .pipe(
            map((response: AjaxResponse<any>): Action => requestMissionPlanByIdSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(requestMissionPlanByIdError(error))
                ),
            ),
          ),
    ),
  );

export const deleteMissionPlanEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteMissionPlan.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .deleteWithHeaders(`/mpt/missionplans/${$action.payload.missionPlanId}`, $action.payload, {
            userid: $action.payload.userId,
          })
          .pipe(
            map((response: AjaxResponse<any>): Action => deleteMissionPlanSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(deleteMissionPlanError(error))
                ),
            ),
          ),
    ),
  );

export const getHnaOverrideDataEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestHnaOverrideData.type),
    switchMap(($action: any): Observable<Action> => {
      return ajaxRaw
        .getWithHeaders(`/mpt/missionplans/${$action.payload.missionId}/hnarequest`, {
          userid: $action.payload.userId,
        })
        .pipe(
          map((response: AjaxResponse<any>): Action => requestHnaOverrideDataSuccess(response)),
          retry({ delay: 5000 }),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestHnaOverrideDataError(error))
              ),
          ),
          takeUntil(action$.pipe(ofType(ActionCreators.CancelRequestHnaOverrideData.type))),
        );
    }),
  );

/**
 * Save the mission plan id to session storage.
 * @param state the current state of the redux store.
 */
const saveSelectedMissionPlanId = (missionPlanId: number): any => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_MISSION_PLAN_ID, `${missionPlanId}`);
  } catch (error) {
    return ActionCreators.SavePerformanceOutlookMissionPlanIdError.create();
  }

  return ActionCreators.SavePerformanceOutlookMissionPlanIdSuccessful.create();
};

/**
 * Save the mission plan id to session storage everytime it changes.
 * @param action$ the actions to look out for any change in the mission plan id state.
 * @param state$ the current redux state. Note: this is the state after the action has gone through
 *               the reducer.
 */
export const setSelectedMissionPlanId = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.SetSelectedMissionPlanId.type),
    switchMap(($action): Observable<Action> => of(saveSelectedMissionPlanId($action.payload))),
  );

/**
 * Clear the mission plan id from session storage.
 * @param state the current state of the redux store.
 */
const clearSelectedMissionPlanId = (): any => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_MISSION_PLAN_ID);
  } catch (error) {
    return ActionCreators.SavePerformanceOutlookMissionPlanIdError.create();
  }

  return ActionCreators.SavePerformanceOutlookMissionPlanIdSuccessful.create();
};

/**
 * Clears the mission plan id from session storage.
 * @param action$ all redux actions.
 * @param state$ the current redux state. Note: this is the state after the action has gone through
 *               the reducer.
 */
export const unsetSelectedMissionPlanId = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.ClearSelectedMissionPlanId.type),
    switchMap(($action): Observable<Action> => of(clearSelectedMissionPlanId())),
  );
